import React, { useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { NumberFormatter } from "react-number-formatter";
import DeleteModal from "../../modal/DeleteModal";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "../../../assets/admin/edit-icon.svg";
import US_States from "../../../model/enum/US_States";
import Contractor from "../../../model/contractor";
import ApiService from "../../../service/ApiService";
import DatabaseEntities from "../../../model/enum/DatabaseEntities";
import AlertService from "../../../service/AlertService";
import ToastMessages from "../../../model/enum/ToastMessages";
import Misc from "../../../utils/Misc";
import useStyles from "./styles";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

const ContractorModal = ({ contractor, resetList }: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = contractor != null;
  const id = edit ? contractor.id : 0;
  const [firstName, setFirstName] = useState(
    edit ? contractor.firstName : null
  );
  const [lastName, setLastName] = useState(edit ? contractor.lastName : null);
  const [email, setEmail] = useState(edit ? contractor.email : null);
  const [phone, setPhone] = useState(edit ? contractor.phone : null);
  const [zipCode, setZipCode] = useState(
    edit && contractor.zip ? contractor?.zip : null
  );
  const [street, setStreet] = useState(
    edit && contractor.street ? contractor.street : null
  );
  const [city, setCity] = useState(
    edit && contractor.city ? contractor.city : null
  );
  const [state, setState] = useState(
    edit && contractor.state ? contractor.state : null
  );
  const [ein, setEin] = useState(
    edit ? contractor.employeerIdentificationNumber : null
  );
  const [companyName, setCompanyName] = useState(
    edit ? contractor.companyName : null
  );
  const [licenseNumber, setLicenseNumber] = useState(
    edit ? contractor.licenseNumber : null
  );
  const [insuranceNumber, setInsuranceNumber] = useState(
    edit ? contractor.insuranceNumber : null
  );
  const [isVerified, setIsVerified] = useState(
    edit ? contractor.isVerified : false
  );
  const [notes, setNotes] = useState(edit ? contractor.notes : null);

  const US_State = new US_States();

  const handleFirstName = (e: any) => setFirstName(e.target.value);
  const handleLastName = (e: any) => setLastName(e.target.value);
  const handleEmail = (e: any) => setEmail(e.target.value);
  const handlePhone = (e: any) => setPhone(e);
  const handleZipCode = (e: any) => {
    e.preventDefault();
    let zip = e.target.value;
    if (!isNaN(zip)) {
      if (US_State.isZipValid(zip)) {
        setCity(US_State.getStateCity(zip));
        setState(US_State.getStateName(zip));
      } else {
        setCity("");
        setState("");
      }
      setZipCode(zip);
    }
  };
  const handleStreet = (e: any) => setStreet(e.target.value);
  const handleCity = (e: any) => setCity(e.target.value);
  const handleState = (e: any) => setState(e.target.value);
  const handleEin = (e: any) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 9) {
      setEin(value);
    }
  };
  const handleCompanyName = (e: any) => setCompanyName(e.target.value);
  const handleLicenseNumber = (e: any) => setLicenseNumber(e.target.value);
  const handleInsuranceNumber = (e: any) => setInsuranceNumber(e.target.value);
  const handleIsVerified = (e: any) => setIsVerified(e.target.checked);
  const handleNotes = (e: any) => setNotes(e.target.value);

  const handleCreate = async (payload: Contractor) => {
    await ApiService.post<Contractor>(DatabaseEntities.CONTRACTORS, payload)
      .then(() => {
        AlertService.success(ToastMessages.CREATED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.CREATE_FAILED);
      });
  };

  const handleUpdate = async (payload: Contractor) => {
    await ApiService.put<Contractor>(
      `${DatabaseEntities.CONTRACTORS}/${payload.id}`,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Contractor) => {
    await ApiService.delete<any>(
      `${DatabaseEntities.CONTRACTORS}/${payload.id}`
    )
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const invalidInput = () => {
    return (
      firstName === null ||
      lastName === null ||
      email === null ||
      !Misc.validEmailFormat(email) ||
      phone === null ||
      street === null ||
      city === null ||
      state === null ||
      zipCode === null
    );
  };

  const register = async () => {
    if (invalidInput()) return;

    const payload: Contractor = {
      id: id,
      firstName: firstName?.trim(),
      lastName: lastName?.trim(),
      email: email,
      phone: phone,
      street: street,
      city: city,
      state: state,
      zip: zipCode,
      employeerIdentificationNumber: Misc.formatEIN(ein),
      companyName: companyName,
      licenseNumber: licenseNumber,
      insuranceNumber: insuranceNumber,
      isVerified: isVerified,
      notes: notes,
    };

    if (edit) {
      await handleUpdate(payload);
    } else {
      await handleCreate(payload);
      setFirstName(null);
      setLastName(null);
      setEmail(null);
      setPhone(null);
      setStreet(null);
      setCity(null);
      setState(null);
      setZipCode(null);
      setEin(null);
      setCompanyName(null);
      setLicenseNumber(null);
      setInsuranceNumber(null);
      setIsVerified(false);
      setNotes(null);
    }

    handleClose();
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <Btn
          className={classes.headerButton}
          variant="contained"
          onClick={handleOpen}
        >
          + Add contractor
        </Btn>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        <Box sx={style}>
          <div className={classes.modalHeader}>
            {edit ? (
              <Typography className={classes.modalTitle}>
                <DeleteModal item={contractor} handleDelete={handleDelete} />
              </Typography>
            ) : (
              <Typography className={classes.modalTitle}>
                Add contractor
              </Typography>
            )}
            <Button onClick={handleClose} sx={{ color: "black" }}>
              <CloseIcon />
            </Button>
          </div>
          <hr />
          <div className={classes.modalContent}>
            <div className={classes.detailGroup}>
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                className={classes.input}
                onChange={handleFirstName}
              />
              <input
                type="text"
                placeholder="Last Name"
                className={classes.input}
                value={lastName}
                onChange={handleLastName}
              />
            </div>
            <div className={classes.detailGroup}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                className={classes.input}
                onChange={handleEmail}
              />
              <div className={classes.input}>
                <NumberFormatter
                  defaultCountry="USA"
                  placeholder="Phone"
                  value={phone}
                  getValue={(n: string) => handlePhone(n)}
                />
              </div>
            </div>
            <div className={classes.detailGroup}>
              <input
                type="text"
                placeholder="Street"
                value={street}
                className={classes.input}
                onChange={handleStreet}
              />
              <input
                className={classes.input}
                value={zipCode}
                onChange={handleZipCode}
                placeholder="ZIP code"
              />
            </div>
            <div className={classes.detailGroup}>
              <input
                type="text"
                placeholder="City"
                value={city}
                className={classes.input}
                onChange={handleCity}
              />
              <input
                type="text"
                placeholder="State"
                value={state}
                className={classes.input}
                onChange={handleState}
              />
            </div>
            <div className={classes.detailGroup}>
              <input
                type="text"
                placeholder="Company Name"
                value={companyName}
                className={classes.input}
                onChange={handleCompanyName}
              />
              <input
                type="text"
                placeholder="E.I.N"
                value={ein}
                className={classes.input}
                onChange={handleEin}
              />
            </div>
            <div className={classes.detailGroup}>
              <input
                type="text"
                placeholder="License Number"
                value={licenseNumber}
                className={classes.input}
                onChange={handleLicenseNumber}
              />
              <input
                type="text"
                placeholder="Insurance Number"
                value={insuranceNumber}
                className={classes.input}
                onChange={handleInsuranceNumber}
              />
            </div>
            <div className={classes.checkboxContainer}>
              <input
                type="checkbox"
                checked={isVerified}
                onChange={handleIsVerified}
                className={classes.checkbox}
                id="isVerified"
              />
              <label htmlFor="isVerified" className={classes.checkboxLabel}>
                Verified
              </label>
            </div>
            <textarea
              placeholder="Notes"
              value={notes}
              className={classes.textarea}
              onChange={handleNotes}
            />
          </div>
          <div className={classes.buttonDiv}>
            <Btn
              variant="contained"
              className={classes.button}
              onClick={register}
              disabled={invalidInput()}
            >
              {edit ? "Save" : "Create"}
            </Btn>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default ContractorModal;
